import { z } from 'zod';
import { FieldTemplateB64Schema } from './field.schema.js';
import { FACE_MODEL_ID } from '../faceModel.js';

/* ============================================================
 * MIGRATING A WORKER OFF THE RETIRED FACE ENGINE — the wire.
 *
 * A device recognised a worker on his `mobilefacenet-192` template, embedded
 * the SAME frame in the current engine, and is handing that embedding over.
 *
 * ## Why this is not /device-enroll with a flag
 *
 * An enrolment is a capture: a person was photographed, for this purpose,
 * now, and somebody attested whose face it was. None of that is true here.
 * Nobody was photographed — the frame was taken to record a punch. Nobody
 * attested — the matcher's own opinion is the only witness. And crucially,
 * nothing is being REPLACED: the worker keeps the legacy template that is
 * currently the only thing recognising him, until the office retires it
 * deliberately.
 *
 * So it gets its own endpoint, its own origin (`AUTO_HARVEST`) and its own
 * lawful basis (`INHERITED_FROM_ENROLMENT` — see fieldTemplateProvenance.ts,
 * which wrote all three rules before any wire accepted them, precisely so the
 * ingest could not be built by copying the enrolment path). A single flag on
 * /device-enroll would have inherited that path's REPLACE-and-version
 * semantics, its dedup override, and its consent stamp of `now`.
 *
 * ## What the server does NOT take on trust
 *
 * Everything on this request is self-asserted by the device: the score, the
 * liveness, the quality. They are stored for the audit trail, not believed as
 * authorisation. The server's own refusals are independent — the worker must
 * already hold an ACTIVE template on a legacy engine (so this can never be a
 * back door to a first enrolment), he must not already hold a current one, and
 * the incoming face is run against the company gallery exactly as an enrolment
 * is. See `field.templateUpgrade.ts`.
 * ============================================================ */

export const FieldTemplateUpgradeItemSchema = z.object({
  /** Device idempotency key for THIS promotion (a replay acks DUPLICATE). */
  uuid: z.string().min(8).max(64),
  /** Employee.employeeCode — the roster syncs to the device by code. */
  workerCode: z.string().min(1).max(32),
  /**
   * ONE template, not a set. An enrolment captures five frames at five poses
   * because it is building a gallery; this is one good frame that happened to
   * be in front of the camera, and pretending otherwise would let a device
   * push five near-identical vectors of the same instant and call it a
   * multi-pose enrolment.
   */
  template: FieldTemplateB64Schema,
  /**
   * THE LEGACY COSINE THAT JUSTIFIED THIS. Required: a template folded back
   * from a match, with no record of how confident that match was, is a row
   * nobody reviewing a bad migration could evaluate. The device's own floor is
   * 0.80 (`DualMatch.PROMOTE_MIN_SCORE`) and the server refuses below it too —
   * not because it believes the number, but because a device sending a lower
   * one is not running the guardrails.
   */
  matchScore: z.number().min(0).max(1),
  /** Graded liveness of the frame (audit). */
  liveness: z.number().min(0).max(1).optional(),
  /** Enrol-scale quality of the frame (audit). */
  quality: z.number().min(0).max(1).optional(),
  /** When the frame was taken, device wall clock, unix millis. */
  takenAt: z.number().int().positive(),
  /** The operator on duty, when there was one. */
  operatorCode: z.string().min(1).max(32).optional(),
  /**
   * Nobody was standing there — a wall panel. Recorded on the row
   * (`capturedUnattended`) exactly as an unattended enrolment is, so a consent
   * trail can tell a designed absence of an operator from a lost one.
   */
  capturedUnattended: z.boolean().optional(),
});
export type FieldTemplateUpgradeItem = z.infer<typeof FieldTemplateUpgradeItemSchema>;

export const FieldTemplateUpgradeRequestSchema = z.object({
  /**
   * The engine that embedded `template`. REQUIRED here, where /device-enroll
   * leaves it optional for wire compatibility: this endpoint did not exist
   * before the engine id did, so there is no old client to be compatible with,
   * and a template whose engine is a guess is the one thing this whole
   * migration is about not doing.
   */
  modelId: z.literal(FACE_MODEL_ID),
  upgrades: z.array(FieldTemplateUpgradeItemSchema).min(1).max(50),
});
export type FieldTemplateUpgradeRequest = z.infer<typeof FieldTemplateUpgradeRequestSchema>;

/**
 * Per-item outcome. Every one of them is TERMINAL for that uuid — the device
 * deletes the queued row on any of them, because none is fixed by retrying
 * the same frame:
 *
 *   UPGRADED          written; the worker now holds a current-engine template.
 *   DUPLICATE         this uuid was already processed.
 *   ALREADY_UPGRADED  another device got there first, or HR re-enrolled him.
 *   NOT_LEGACY        he has no legacy template to be migrated off, so this is
 *                     not an upgrade and will not be accepted as one.
 *   DEDUP_BLOCKED     the face reaches another worker's gallery. Raised to HR;
 *                     there is nobody to override it and the device must not
 *                     keep asking.
 *   REFUSED           the request itself is not one this server will act on
 *                     (below the confidence floor, unknown worker, off-roster).
 */
export const FieldTemplateUpgradeOutcomeSchema = z.object({
  uuid: z.string(),
  status: z.enum([
    'UPGRADED',
    'DUPLICATE',
    'ALREADY_UPGRADED',
    'NOT_LEGACY',
    'DEDUP_BLOCKED',
    'REFUSED',
  ]),
  /** Why, for the device log. Never shown to a worker — nothing here is theirs to fix. */
  message: z.string().optional(),
});
export type FieldTemplateUpgradeOutcome = z.infer<typeof FieldTemplateUpgradeOutcomeSchema>;

export const FieldTemplateUpgradeResponseSchema = z.object({
  results: z.array(FieldTemplateUpgradeOutcomeSchema),
  /**
   * HOW MANY WORKERS ON THIS DEVICE'S ROSTER ARE STILL LEGACY-ONLY, after this
   * batch. The device does not compute it — it cannot, it only holds one
   * site's gallery and cannot see what other gates have migrated — and it is
   * on this response rather than on /sync because this is the request that
   * changes it. When it reaches 0 for every site, the owner can retire the
   * legacy gallery and the second inference stops.
   */
  remaining: z.number().int().nonnegative(),
});
export type FieldTemplateUpgradeResponse = z.infer<typeof FieldTemplateUpgradeResponseSchema>;
